/**
 * +atmos: the outdoor atmosphere: sky dome, painted cumulus, the distant ranges, far LOD stand-ins,
 * light shafts, cloud shadows and the time of day that drives them all (plus the key / fill
 * lights, fog and grade). field.ts builds one per open map (`buildAtmosphere`); the renderer calls
 * `atmosphere.apply(lights)` once a frame (before the capture hook, which may override it).
 * Presentation only: nothing here touches the sim.
 */
import * as THREE from 'three';
import { U, setToneTint } from '../../style';
import type { OutPlacement } from '../layout';
import { buildClouds } from './clouds';
import { type FarTreeIn, buildFarLod, coniferStandIn } from './farLod';
import { buildFarWorld } from './farWorld';
import { buildShafts, type ShaftSpot } from './shafts';
import { skyDome } from './sky';
import { type AtmosLook, DAY_SUN, type DayInputs, type TodMode, type TodPreset, cycleAt, cyclePhase, dayLook, isNight, mixLook, presetLook } from './timeOfDay';
import { ATMOS } from './uniforms';

export { ATMOS, CLOUD_SHADOW_GLSL } from './uniforms';
export { MOSSVALE_SHAFT_SPOTS } from './shafts';
export type { TodMode, TodPreset } from './timeOfDay';

/** Camera far plane on open maps (the whole zone and the ranges around it). */
export const OUTDOOR_FAR = 2600;
/** Far-stage fog on open maps (style.ts uFogEnd). */
const FOG_END = new THREE.Vector3(1900, 2500, 0.74);
const DUNGEON_FOG_END = new THREE.Vector3(285, 385, 0.9);
/** Far LOD reach per quality preset (yd). */
const LOD_FAR = { low: 420, medium: 900, high: 1700 } as const;

export interface AtmosLights {
  key: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  grade: { uSat: THREE.IUniform<number>; uGain: THREE.IUniform<THREE.Vector3>; uLift: THREE.IUniform<THREE.Vector3> };
  keyDir: THREE.Vector3;
}

export interface AtmosphereOpts {
  day: DayInputs;
  cloudCover: number;
  /** Terrain grid extent (sim yd) and height sampler. */
  extent: { x0: number; y0: number; x1: number; y1: number };
  heightAt(x: number, y: number): number;
  towerDir: { x: number; y: number } | null;
  placements: readonly OutPlacement[];
  /** The field's instanced far forest (stand-ins carry it past FAR_TREE_DRAW). */
  farTrees?: readonly FarTreeIn[];
  leaf: number;
  shafts: readonly ShaftSpot[];
  /** Distant-range palette. */
  ranges?: { rock: number; forest: number; snow: number; grass: number };
}

export interface AtmosphereBuild {
  group: THREE.Group;
  /** Sky colours shared with the water (updated in place). */
  waterSky: { low: THREE.Color; high: THREE.Color; sun: THREE.Vector3 };
  /** World anchor for the Tower (sim x / y, ground height), or null. */
  towerAt(x: number, y: number): number;
  farLodCount: number;
  /** Tint an unlit far-away object's materials (the Tower) with the time of day. */
  tintLand(root: THREE.Object3D): void;
  /** Per frame (positions: sky and clouds follow the camera). */
  frame(camera: THREE.Camera): void;
  dispose(): void;
}

interface Live {
  day: AtmosLook;
  sky: ReturnType<typeof skyDome>;
  clouds: ReturnType<typeof buildClouds>;
  world: ReturnType<typeof buildFarWorld>;
  lod: ReturnType<typeof buildFarLod>;
  shafts: ReturnType<typeof buildShafts> | null;
  /** The theme's aerial-perspective max (uFogShape.x) at day. */
  hazeMax: number;
  /** Unlit far-away materials (the Tower) tinted by the time of day. */
  landTinted: Set<THREE.Material & { color: THREE.Color }>;
}

type Quality = keyof typeof LOD_FAR;
const TONE_L: [number, number, number] = [1, 1, 1];
const TONE_S: [number, number, number] = [1, 1, 1];

class AtmosControl {
  /** Settings: real-time cycle or always day. */
  mode: TodMode = 'cycle';
  /** Capture / debug: a fixed preset or phase (null = follow `mode`). */
  override: TodPreset | null = null;
  phase: number | null = null;
  quality: Quality = 'high';
  /** Current night state (ambience beds); `onNight` fires on changes. */
  night = false;
  onNight: ((night: boolean) => void) | null = null;
  readonly look: AtmosLook = dayLook({ sky: { top: 0, mid: 0, horizon: 0, cloud: 0, cloudShade: 0 }, lighting: { fog: [0, 0, 0], hemiSky: 0, hemiGround: 0, hemiIntensity: 0, key: 0, lift: [0, 0, 0] } });
  live: Live | null = null;
  private lights: AtmosLights | null = null;
  private lastT = -1;

  setQuality(q: Quality): void {
    this.quality = q;
    if (!this.live) return;
    this.live.lod.setFar(LOD_FAR[q]);
    if (this.live.shafts) this.live.shafts.mesh.visible = q === 'high';
  }

  /** The look for now (presentation clock), blended between presets. */
  private target(): AtmosLook {
    const L = this.live!;
    const of = (p: TodPreset): AtmosLook => (p === 'day' ? L.day : presetLook(p));
    if (this.override) return of(this.override);
    if (this.mode === 'day' && this.phase === null) return L.day;
    const c = cycleAt(this.phase ?? cyclePhase(Date.now()));
    return mixLook(of(c.a), of(c.b), c.t);
  }

  /** Once a frame: drive lights, fog, grade, sky, clouds, ranges, shafts and cloud shadows. Returns false off open maps. */
  apply(lights: AtmosLights): boolean {
    const L = this.live;
    if (!L) return false;
    this.lights = lights;
    const k = mixLook(this.look, this.target(), 1, this.look);
    lights.keyDir.set(k.sun[0], k.sun[1], k.sun[2]).normalize();
    ATMOS.uSunDir.value.copy(lights.keyDir);
    lights.key.color.setRGB(k.key[0], k.key[1], k.key[2]);
    lights.key.intensity = k.keyIntensity;
    U.uKeyIntensity.value = k.keyIntensity / Math.PI;
    lights.hemi.color.setRGB(k.hemiSky[0], k.hemiSky[1], k.hemiSky[2]);
    lights.hemi.groundColor.setRGB(k.hemiGround[0], k.hemiGround[1], k.hemiGround[2]);
    lights.hemi.intensity = k.hemiIntensity;
    U.uFogColor.value.setRGB(k.fog[0], k.fog[1], k.fog[2]);
    U.uFogTint.value.setRGB(k.fogTint[0], k.fogTint[1], k.fogTint[2]);
    U.uFogEnd.value.copy(FOG_END);
    lights.grade.uSat.value = k.sat;
    lights.grade.uGain.value.set(k.gain[0], k.gain[1], k.gain[2]);
    lights.grade.uLift.value.set(k.lift[0], k.lift[1], k.lift[2]);
    const sk = k.keyIntensity / 3.1;
    ATMOS.uSunColor.value.setRGB(k.key[0] * sk, k.key[1] * sk, k.key[2] * sk);
    ATMOS.uSkyColor.value.setRGB(k.skyHorizon[0], k.skyHorizon[1], k.skyHorizon[2]);
    ATMOS.uDay.value = 1 - k.night;
    // Cloud shadows drift with the clouds' wind.
    const t = U.uTime.value;
    const dt = this.lastT < 0 ? 0 : Math.max(0, Math.min(0.2, t - this.lastT));
    this.lastT = t;
    const cs = ATMOS.uCloudShadow.value;
    cs.y = (cs.y - dt * 2.2) % 3000;
    cs.z = (cs.z - dt * 1.1) % 3000;
    cs.w = k.cloudShadow;
    L.sky.u.uTop.value.setRGB(k.skyTop[0], k.skyTop[1], k.skyTop[2]);
    L.sky.u.uMid.value.setRGB(k.skyMid[0], k.skyMid[1], k.skyMid[2]);
    L.sky.u.uHorizon.value.setRGB(k.skyHorizon[0], k.skyHorizon[1], k.skyHorizon[2]);
    L.sky.u.uNight.value = k.night;
    L.clouds.u.uCloud.value.setRGB(k.cloud[0], k.cloud[1], k.cloud[2]);
    L.clouds.u.uShade.value.setRGB(k.cloudShade[0], k.cloudShade[1], k.cloudShade[2]);
    L.clouds.u.uHorizon.value.setRGB(k.skyHorizon[0], k.skyHorizon[1], k.skyHorizon[2]);
    L.clouds.u.uNight.value = k.night;
    L.world.uLand.value.setRGB(k.land[0], k.land[1], k.land[2]);
    if (L.shafts) L.shafts.uStrength.value = k.shafts;
    for (const m of L.landTinted) m.color.setRGB(k.land[0], k.land[1], k.land[2]);
    U.uFogShape.value.x = L.hazeMax * k.haze;
    // Toon tones: the lit band follows the key's colour / strength, the shadow band the fill's.
    const d = L.day;
    const cl = (v: number): number => Math.min(1.4, Math.max(0.05, v));
    const ki = k.keyIntensity / d.keyIntensity;
    const hi = k.hemiIntensity / d.hemiIntensity;
    TONE_L[0] = cl((k.key[0] / Math.max(0.02, d.key[0])) * ki);
    TONE_L[1] = cl((k.key[1] / Math.max(0.02, d.key[1])) * ki);
    TONE_L[2] = cl((k.key[2] / Math.max(0.02, d.key[2])) * ki);
    TONE_S[0] = cl(Math.sqrt((k.hemiSky[0] / Math.max(0.02, d.hemiSky[0])) * hi));
    TONE_S[1] = cl(Math.sqrt((k.hemiSky[1] / Math.max(0.02, d.hemiSky[1])) * hi));
    TONE_S[2] = cl(Math.sqrt((k.hemiSky[2] / Math.max(0.02, d.hemiSky[2])) * hi));
    setToneTint(TONE_L, TONE_S);
    const night = isNight(k);
    if (night !== this.night) {
      this.night = night;
      this.onNight?.(night);
    }
    return true;
  }

  /** Back to the dungeon defaults (called when the open map goes away). */
  release(): void {
    this.live = null;
    this.lastT = -1;
    setToneTint([1, 1, 1], [1, 1, 1]);
    ATMOS.uCloudShadow.value.w = 0;
    ATMOS.uDay.value = 1;
    U.uFogEnd.value.copy(DUNGEON_FOG_END);
    this.lights?.keyDir.set(DAY_SUN[0], DAY_SUN[1], DAY_SUN[2]).normalize();
    ATMOS.uSunDir.value.set(DAY_SUN[0], DAY_SUN[1], DAY_SUN[2]).normalize();
    if (this.night) {
      this.night = false;
      this.onNight?.(false);
    }
  }
}

/** The one atmosphere controller (settings, capture and the renderer talk to it). */
export const atmosphere = new AtmosControl();
if (typeof globalThis !== 'undefined') (globalThis as { __atmos?: AtmosControl }).__atmos = atmosphere;

export function buildAtmosphere(o: AtmosphereOpts): AtmosphereBuild {
  const group = new THREE.Group();
  group.name = 'atmosphere';
  const day = dayLook(o.day);
  const sky = skyDome(o.day.sky);
  group.add(sky.mesh);
  const clouds = buildClouds({ cloud: o.day.sky.cloud, shade: o.day.sky.cloudShade, horizon: o.day.sky.horizon }, o.cloudCover);
  group.add(clouds.mesh);
  const tree = coniferStandIn(o.leaf, 10);
  const world = buildFarWorld({ ...o.extent, heightAt: o.heightAt, towerDir: o.towerDir, tree, ...(o.ranges ? { colors: o.ranges } : {}) });
  group.add(world.group);
  const lod = buildFarLod(o.placements, o.leaf, o.farTrees ?? []);
  group.add(lod.group);
  const shafts = o.shafts.length ? buildShafts(o.shafts) : null;
  if (shafts) group.add(shafts.mesh);
  const landTinted = new Set<THREE.Material & { color: THREE.Color }>();
  atmosphere.live = { day, sky, clouds, world, lod, shafts, hazeMax: o.day.lighting.fogShape?.[0] ?? 0.9, landTinted };
  mixLook(day, day, 0, atmosphere.look);
  atmosphere.setQuality(atmosphere.quality);
  const mats = [sky.mesh.material as THREE.Material];
  return {
    group,
    waterSky: { low: sky.u.uHorizon.value, high: sky.u.uMid.value, sun: ATMOS.uSunDir.value },
    towerAt: (x, y) => world.heightAt(x, y),
    farLodCount: lod.count,
    tintLand(root) {
      root.traverse((ob) => {
        const m = (ob as THREE.Mesh).material as (THREE.Material & { color?: THREE.Color; blending?: number }) | undefined;
        if (m && !Array.isArray(m) && m.color && m.blending === THREE.NormalBlending) landTinted.add(m as THREE.Material & { color: THREE.Color });
      });
    },
    frame(camera) {
      sky.mesh.position.copy(camera.position);
    },
    dispose() {
      if (atmosphere.live?.sky === sky) atmosphere.release();
      group.removeFromParent();
      sky.mesh.geometry.dispose();
      for (const m of mats) m.dispose();
      clouds.dispose();
      world.dispose();
      lod.dispose();
      shafts?.dispose();
      tree.dispose();
    },
  };
}
